import { FormBlock } from '@qbe/ads-blocks-forms';
import { Box, Button, RadioSelectableCard, Typography } from '@qbe/ads-core';
import React, { FC, useState } from 'react';

import Icon from '~/components-ds/Icon/Icon';
import { useAppContent, useAppDispatch, useBusinessDetails } from '~/hooks';
import { InsuredType } from '~/slices/businessDetails';
import { qalFormStartAction } from '~/thunks/analytics';
import { updateInsuredTypeAction } from '~/thunks/state-actions';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { ContextualHelpWithButton } from '../../../components-ds/ContextualHelp/ContextualHelpWithButton';

type Props = {
  disableContent?: boolean;
};

export const WhatToInsure: FC<Props> = ({ disableContent = false }) => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.businessDetailsWhatToInsure',
  });

  const { insuredType } = useBusinessDetails();
  const [type, setType] = useState<InsuredType | undefined>(insuredType ?? undefined);

  const dispatch = useAppDispatch();

  const handleContinue = () => {
    if (type && type !== insuredType) dispatch(updateInsuredTypeAction(type));
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <Box>
      <RadioSelectableCard
        disabled={typeof disableContent !== 'undefined' ? disableContent : true}
        title={getTextContent('fields.selectInsuranceType.title')}
        titleSuffix={
          <ContextualHelpWithButton tracking="help: What are you looking to insure?">
            <Typography gutterBottom>
              {getJSXContent('contextualhelpers.selectInsuranceType.description')}
            </Typography>
          </ContextualHelpWithButton>
        }
        name="tile"
        labelPlacement="top"
        direction="horizontal"
        columns={2}
        iconColors="primary"
        defaultValue={type ?? ''}
        onChange={(value) => {
          if (value !== type) {
            setType(value as InsuredType);
            dispatch(updateInsuredTypeAction(undefined));
          }
          handleFormStart();
        }}
        radioList={[
          {
            label: getTextContent('fields.selectInsuranceType.businessOperator'),
            id: `business-operator`,
            value: `business-operator`,
            customComponent: (
              <Icon
                name="insuredTypeBusinessOperator"
                alt="Business operator"
                width="48px"
                height="48px"
              />
            ),
            'data-track': 'A small business operation',
          },
          {
            label: getTextContent('fields.selectInsuranceType.landlord'),
            id: `landlord`,
            value: `landlord`,
            customComponent: (
              <Icon name="insuredTypeLandlord" alt="Landlord" width="48px" height="48px" />
            ),
            'data-track': 'A commercial property only',
          },
        ]}
      />

      <FormBlock preset="button">
        <Button
          data-track="continue"
          onClick={handleContinue}
          disabled={!type || type === insuredType}
          label={getSharedContent('continueStepButttonLabel')}
        />
      </FormBlock>
    </Box>
  );
};
